import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable bottom sheet — slides up from the bottom of the viewport.
 * Renders into a portal on document.body. ESC and scrim-click close it.
 *
 * Props:
 *   open      — whether the sheet is visible
 *   onClose   — close handler
 *   className — extra class(es) on the sheet panel
 *   children  — sheet content
 */
export default function BottomSheet({ open, onClose, className = '', children }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="bottom-sheet-scrim"
      onClick={() => onCloseRef.current()}
      role="presentation"
    >
      <div
        className={['bottom-sheet', className].filter(Boolean).join(' ')}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
